import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Users, Bot, Puzzle, Clock, type LucideIcon } from "lucide-react";
import {
  fetchAllClients,
  getDashboardStats,
  getAuditLogs,
  type DashboardStats,
  type AuditLogEntry,
} from "@/lib/api";
import type { SerializedBusiness } from "@/types";
import { EntityAvatar } from "@/components/EntityAvatar";
import { EntitySection } from "@/components/EntitySection";
import { AuditLogOutcomeBadge } from "@/components/AuditLogOutcomeBadge";

export const Route = createFileRoute("/dashboard/")({
  component: RouteComponent,
});

function RouteComponent() {
  return <DashboardIndexPageComponent />;
}

function StatCard({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: LucideIcon;
  label: string;
  value: number | string;
  detail?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardDescription>{label}</CardDescription>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {detail && <p className="mt-1 truncate text-xs text-muted-foreground">{detail}</p>}
      </CardContent>
    </Card>
  );
}

// Exported for testing
export function DashboardIndexPageComponent() {
  const [clients, setClients] = useState<SerializedBusiness[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState<string | null>(null);

  const [stats, setStats] = useState<DashboardStats | null>(null);

  const [recentActivity, setRecentActivity] = useState<AuditLogEntry[]>([]);
  // Support-role users (see DRA-220) can't call GET /api/audit-logs
  // (requireSuperAdminRole) - that 403 is expected, not an error state, so
  // the widget just stays hidden for them rather than showing anything.
  const [activityVisible, setActivityVisible] = useState(false);

  useEffect(() => {
    let active = true;
    fetchAllClients()
      .then((response) => {
        if (!active) return;
        setClients(response.clients);
        setClientsLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching clients:", err);
        setClientsError("Failed to load clients.");
        setClientsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getDashboardStats()
      .then((result) => {
        if (!active) return;
        setStats(result);
      })
      .catch((err) => {
        console.error("Error fetching dashboard stats:", err);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getAuditLogs({ limit: 5 })
      .then((result) => {
        if (!active) return;
        setRecentActivity(result.logs);
        setActivityVisible(true);
      })
      .catch((err) => {
        console.error("Error fetching recent activity:", err);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <div>
          <h1 className="text-4xl font-bold mb-2">Rivermount Admin Dashboard</h1>
          <p className="text-lg text-muted-foreground">
            Live overview of clients, agents, and platform activity.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Clients" value={clients.length} />
          <StatCard
            icon={Bot}
            label="Active Agents"
            value={stats?.totalActiveAgents ?? "—"}
            detail={
              stats && stats.agentsByType.length > 0
                ? stats.agentsByType.map((entry) => `${entry.typeId} (${entry.count})`).join(" • ")
                : undefined
            }
          />
          <StatCard icon={Puzzle} label="Active Integrations" value={stats?.activeIntegrations ?? "—"} />
        </div>

        <div className={activityVisible ? "grid grid-cols-1 lg:grid-cols-3 gap-8" : ""}>
          <div className={activityVisible ? "lg:col-span-2" : ""}>
            <EntitySection
              icon={Users}
              title="Clients"
              count={clients.length}
              manageLabel="Create Client"
              manageHref="/dashboard/clients/new"
              isEmpty={!clientsLoading && !clientsError && clients.length === 0}
              emptyMessage='No clients yet. Click "Create Client" to add one.'
            >
              {clientsLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Loading...</div>
              ) : clientsError ? (
                <div className="p-4 text-sm text-destructive">{clientsError}</div>
              ) : (
                clients.map((client) => (
                  <Link
                    key={client.id}
                    to="/dashboard/clients/$id"
                    params={{ id: client.id }}
                    className="flex items-center gap-3 p-3 transition-colors hover:bg-accent"
                  >
                    <EntityAvatar type="client" icon={client.icon} name={client.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{client.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{client.email}</p>
                    </div>
                  </Link>
                ))
              )}
            </EntitySection>
          </div>

          {activityVisible && (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-sm font-semibold text-foreground">Recent Activity</h2>
                </div>
                <Link to="/dashboard/audit-logs" className="text-xs text-muted-foreground hover:underline">
                  View all
                </Link>
              </div>
              {recentActivity.length === 0 ? (
                <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                  No recent activity.
                </p>
              ) : (
                <div className="divide-y overflow-hidden rounded-md border">
                  {recentActivity.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between gap-3 p-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {entry.method ? `${entry.method} ${entry.url}` : entry.action || "—"}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {entry.actorEmail || entry.actorUid || "—"}
                          {entry.createdAt ? ` • ${new Date(entry.createdAt).toLocaleString()}` : ""}
                        </p>
                      </div>
                      <AuditLogOutcomeBadge entry={entry} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
